// Translates between the visual builder's { nodes, edges } graph and the
// backend's linear { trigger, steps } journey config.
//
// The backend engine (src/services/journeys/engine.service.js) supports
// segment/event/schedule triggers and a linear chain of steps — sends,
// relative/absolute waits, goal checks, and exactly one branch (Condition
// Split or A/B Split) per step, whose two outputs always reconverge to the
// same next step — there is no real diverging-path execution. This module
// walks the graph the user actually drew (instead of creation order) and
// enforces that shape: a branch node's two outputs must each be either a
// single action node or a direct pass-through (no action on that side —
// "if delivered, just continue"), and both must lead back to the same
// downstream node, or saving throws a clear error rather than building a
// config that would silently mis-execute.

import { propFiltersToProperties, propertiesToPropFilters } from './segmentConditions';
import { defaultCfg } from '../data/nodeDefinitions';

const ACTION_NODE_TO_CHANNEL = {
  'action-push': 'push',
  'action-whatsapp': 'whatsapp',
  'action-email': 'email',
  'action-sms': 'sms',
  'action-rcs': 'rcs',
};

const CHANNEL_TO_ACTION_NODE = Object.fromEntries(
  Object.entries(ACTION_NODE_TO_CHANNEL).map(([node, channel]) => [channel, node])
);

const CONDITION_TYPES = ['if_not_opened', 'if_delivered', 'if_failed', 'event_performed', 'event_not_performed', 'user_property'];

function splitParams(csv) {
  return (csv || '').split(',').map((s) => s.trim()).filter(Boolean);
}

// node.cfg shapes match campaign.validator.js's real per-channel Joi schemas
// (except action-inapp, which has no campaign equivalent) — see
// data/nodeDefinitions.js's defaultCfg(). This builds the exact `template`
// shape the backend (and the real provider layer) expects.
function buildTemplate(nodeType, cfg) {
  switch (nodeType) {
    case 'action-whatsapp':
      return {
        templateName: cfg.templateName || '',
        templateParams: splitParams(cfg.templateParams),
        ...(cfg.imageUrl ? { imageUrl: cfg.imageUrl } : {}),
      };
    case 'action-sms':
      return { text: cfg.text || '', ...(cfg.templateId ? { templateId: cfg.templateId } : {}) };
    case 'action-email':
      return { subject: cfg.subject || '', html: cfg.html || '' };
    case 'action-rcs':
      return { templateId: cfg.templateId || '', templateParams: splitParams(cfg.templateParams) };
    case 'action-push':
      return {
        title: cfg.title || '', body: cfg.body || '',
        ...(cfg.redirectionType ? { redirectionType: cfg.redirectionType } : {}),
        ...(cfg.redirectionValue ? { redirectionValue: cfg.redirectionValue } : {}),
        ...(cfg.imageUrl ? { imageUrl: cfg.imageUrl } : {}),
      };
    default:
      return cfg;
  }
}

// attributionLinkId is a sibling of the step/action object (step.attribution_link_id),
// not part of `template` itself — see buildActionStepAction's own comment.
// Only merged in for the channels ShortLinkField actually renders on.
function templateToCfg(channel, template, attributionLinkId) {
  const t = template || {};
  const linkCfg = ['sms', 'whatsapp', 'rcs'].includes(channel) ? { attributionLinkId: attributionLinkId || '' } : {};
  switch (channel) {
    case 'whatsapp':
      return { templateName: t.templateName || '', templateParams: (t.templateParams || []).join(', '), imageUrl: t.imageUrl || '', ...linkCfg };
    case 'sms':
      return { text: t.text || '', templateId: t.templateId || '', ...linkCfg };
    case 'email':
      return { subject: t.subject || '', html: t.html || '' };
    case 'rcs':
      return { templateId: t.templateId || '', templateParams: (t.templateParams || []).join(', '), ...linkCfg };
    case 'push':
    default:
      return { title: t.title || '', body: t.body || '', redirectionType: t.redirectionType || '', redirectionValue: t.redirectionValue || '', imageUrl: t.imageUrl || '' };
  }
}

function successorId(nodeId, handle, edges) {
  const edge = edges.find((e) => e.from === nodeId && (e.fromHandle || 'out') === (handle || 'out'));
  return edge ? edge.to : null;
}

function buildWaitUntil(cfg) {
  const wu = { rule: cfg.rule, ...(cfg.tz ? { tz: cfg.tz } : {}) };
  if (cfg.rule === 'custom' || cfg.rule === 'specific') wu.custom = { date: cfg.customDate, time: cfg.customTime };
  if (['tomorrow', 'n_days', 'next_weekday', 'monthly_day', 'last_day_of_month'].includes(cfg.rule)) wu.time = cfg.time || '09:00';
  if (cfg.rule === 'n_days') wu.days = Number(cfg.days) || 1;
  if (cfg.rule === 'next_weekday') wu.weekday = Number(cfg.weekday);
  if (cfg.rule === 'monthly_day') wu.day_of_month = Number(cfg.dayOfMonth) || 1;
  if (cfg.rule === 'last_day_of_month') wu.days_before_end = Number(cfg.daysBeforeEnd) || 0;
  return wu;
}

// Repeat-on-Schedule compiled from an action node's config — the send re-fires
// on this schedule while the trip sits on the step, until a stop condition.
function buildRepeat(cfg) {
  return {
    every: cfg.repeatEvery || 'day',
    at: cfg.repeatAt || '09:00',
    ...(cfg.repeatMax ? { max_occurrences: Number(cfg.repeatMax) } : {}),
    ...(cfg.repeatEndDate ? { end_date: cfg.repeatEndDate } : {}),
  };
}

// Per-send time-of-day window (IST) — send only within [start,end); otherwise
// the engine holds the send until the window opens. Powers "T1 morning / T2
// evening" style touches.
function buildSendWindow(cfg) {
  return { start: Number(cfg.sendWindowStart) || 0, end: Number(cfg.sendWindowEnd) || 24 };
}

// Journey-wide shared sending rules compiled from the Entry node's config.
function buildSendingPolicy(cfg) {
  const p = {};
  if (cfg.quietEnabled) p.quiet_hours = { enabled: true, start: Number(cfg.quietStart) || 22, end: Number(cfg.quietEnd) || 8 };
  if (cfg.minSpacingValue) p.min_spacing = { value: Number(cfg.minSpacingValue), unit: cfg.minSpacingUnit || 'hours' };
  if (cfg.capPerDay) p.caps = [{ channel: cfg.capChannel || 'any', per: 'day', max: Number(cfg.capPerDay) }];
  if (cfg.waDedupHours) p.wa_dedup_hours = Number(cfg.waDedupHours);
  if (p.caps || p.min_spacing) p.on_cap = cfg.onCap || 'skip';
  return Object.keys(p).length ? p : null;
}

// Journey-wide global exit compiled from the Entry node's config.
function buildGlobalExit(cfg) {
  const ge = {};
  if (cfg.globalExitEvent) ge.on_event = { event: cfg.globalExitEvent, match_context: !!cfg.globalExitMatchContext };
  if (cfg.globalExitDuration) ge.max_duration = { value: Number(cfg.globalExitDuration) || 1, unit: cfg.globalExitDurationUnit || 'days' };
  return Object.keys(ge).length ? ge : null;
}

function buildTrigger(entryNode) {
  const cfg = entryNode?.cfg || {};
  if (!entryNode || (entryNode.type !== 'entry' && !entryNode.type.startsWith('entry-'))) {
    throw new Error('Add an Entry node before saving.');
  }

  // Unified Entry block — one node, mode picks how the audience/timing compile
  // into the engine's existing discriminated trigger shapes. Attribute-change /
  // manual-list modes are disabled in the UI (engine can't run them yet), so a
  // saved Entry only ever reaches segment/event/schedule here.
  if (entryNode.type === 'entry') {
    const mode = cfg.mode || 'segment';
    const extra = {
      re_entry: cfg.reEntry || 'once_ever',
      ...(cfg.reEntry === 'cooldown' ? { re_entry_cooldown: { value: Number(cfg.cooldownValue) || 30, unit: cfg.cooldownUnit || 'days' } } : {}),
      ...(cfg.context ? { context: cfg.context } : {}),
    };
    if (mode === 'segment') {
      if (!cfg.segmentId) throw new Error('Pick an audience segment for the Entry.');
      return { type: 'segment', segment_id: cfg.segmentId, evaluate_every: '15min', membership_mode: cfg.membershipMode || 'current_and_future', ...extra };
    }
    if (mode === 'event') {
      if (!cfg.event) throw new Error('Pick a trigger event for the Entry.');
      return { type: 'event', event_name: cfg.event, lookback_window: { value: Number(cfg.lookbackValue) || 7, unit: cfg.lookbackUnit || 'days' }, properties: propFiltersToProperties(cfg.propFilters), ...extra };
    }
    if (mode === 'schedule') {
      if (!cfg.cron) throw new Error('Enter a run schedule (cron) for the scheduled Entry.');
      if (!cfg.segmentId) throw new Error('Pick an audience segment for the scheduled Entry.');
      return { type: 'schedule', cron_expression: cfg.cron, timezone: cfg.tz, segment_id: cfg.segmentId, ...extra };
    }
    throw new Error("This Entry mode isn't run by the journey engine yet — switch to Segment, Event, or Scheduled.");
  }

  if (entryNode.type === 'entry-segment') {
    if (!cfg.segmentId) throw new Error('Pick a segment for the entry trigger.');
    return { type: 'segment', segment_id: cfg.segmentId, evaluate_every: '15min' };
  }
  if (entryNode.type === 'entry-event') {
    if (!cfg.event) throw new Error('Pick a trigger event for the Event Trigger.');
    return {
      type: 'event',
      event_name: cfg.event,
      lookback_window: { value: Number(cfg.lookbackValue) || 7, unit: cfg.lookbackUnit || 'days' },
      properties: propFiltersToProperties(cfg.propFilters),
    };
  }
  if (entryNode.type === 'entry-schedule') {
    if (!cfg.cron) throw new Error('Enter a cron expression for the Schedule Entry.');
    if (!cfg.segmentId) throw new Error('Pick a segment for the Schedule Entry.');
    return { type: 'schedule', cron_expression: cfg.cron, timezone: cfg.tz, segment_id: cfg.segmentId };
  }
  throw new Error(`"${entryNode.type}" entry triggers aren't supported by the journey engine yet.`);
}

// Serializes the exact builder graph (node positions, edges, viewport) so a
// reloaded journey renders precisely as drawn — free node placement, notes, and
// even nodes the executable compiler skips all survive the round-trip. The
// backend stores this verbatim under config.ui and the engine ignores it; only
// config.steps drives execution. See graphFromConfig for the reverse.
function buildUiSnapshot(nodes, edges, viewport) {
  return {
    nodes: nodes.map((n) => ({ id: n.id, type: n.type, x: n.x, y: n.y, cfg: n.cfg })),
    edges: (edges || []).map((e) => ({ id: e.id, from: e.from, to: e.to, fromHandle: e.fromHandle || 'out' })),
    ...(viewport ? { viewport } : {}),
  };
}

// Prefers the faithful builder snapshot (config.ui) when present; falls back to
// best-effort auto-layout reconstruction for legacy journeys saved before the
// snapshot existed. Returns { nodes, edges, viewport }.
export function graphFromConfig(config) {
  const ui = config?.ui;
  if (ui?.nodes?.length) {
    return {
      nodes: ui.nodes.map((n) => ({ id: n.id, type: n.type, x: n.x, y: n.y, cfg: n.cfg })),
      edges: (ui.edges || []).map((e) => ({ id: e.id, from: e.from, to: e.to, fromHandle: e.fromHandle || 'out' })),
      viewport: ui.viewport || null,
    };
  }
  const { nodes, edges } = journeyConfigToNodes(config);
  return { nodes, edges, viewport: null };
}

export function nodesToJourneyConfig(nodes, edges = [], viewport = null) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const entryNode = nodes.find((n) => n.type === 'entry' || n.type.startsWith('entry-'));
  const trigger = buildTrigger(entryNode);

  const steps = [];
  const skipped = [];
  const nodeSteps = {};           // builder node id -> step number (Live View overlay)
  const stepByNode = new Map();   // memoized: real node id -> step number (DAG joins)
  const compiling = new Set();    // DFS stack for cycle detection
  const reached = new Set([entryNode.id]);
  let stepNumber = 0;

  // Follow an edge target, transparently absorbing wait nodes, to the next REAL
  // node and compile it. Returns its step number, or null for a dangling/exit end.
  // Waits are attached to the step they precede; prevActionStepId (the action
  // immediately upstream) rides through waits so a delivery-condition still
  // "directly follows" its action across an intervening wait.
  function reach(startId, prevActionStepId) {
    let id = startId;
    let waitAfter = null;
    let waitUntil = null;
    const waitNodeIds = [];
    while (id) {
      reached.add(id);
      const n = byId.get(id);
      if (!n) return null;
      if (n.type === 'wait-relative') { waitAfter = { value: Number(n.cfg.amount) || 1, unit: n.cfg.unit || 'hours' }; waitNodeIds.push(id); id = successorId(id, 'out', edges); continue; }
      if (n.type === 'wait-absolute') { waitUntil = buildWaitUntil(n.cfg); waitNodeIds.push(id); id = successorId(id, 'out', edges); continue; }
      if (n.type === 'logic-exit') return null;
      return compileNode(id, { waitAfter, waitUntil, waitNodeIds }, prevActionStepId);
    }
    return null;
  }

  // Compile a real node into a step and return its number. Memoized so a
  // reconverging join compiles the shared downstream once (both branches point at
  // the same step). Every non-branch step carries an explicit `next` pointer;
  // branch outcomes carry `goto` — so paths may DIVERGE (not just reconverge) and
  // step order no longer matters. Legacy configs (no `next`/`goto`) still run via
  // the engine's index+1 fallback.
  function compileNode(nodeId, waits, prevActionStepId) {
    if (stepByNode.has(nodeId)) return stepByNode.get(nodeId);
    if (compiling.has(nodeId)) throw new Error('This journey has a loop — the engine runs a directed graph without cycles (use Repeat on a send step for recurring messages).');
    compiling.add(nodeId);
    reached.add(nodeId);
    const node = byId.get(nodeId);
    const num = ++stepNumber;
    stepByNode.set(nodeId, num);
    nodeSteps[nodeId] = num;
    (waits?.waitNodeIds || []).forEach((w) => { nodeSteps[w] = num; });
    const step = {
      step: num,
      ...(waits?.waitAfter ? { wait_after: waits.waitAfter } : {}),
      ...(waits?.waitUntil ? { wait_until: waits.waitUntil } : {}),
    };
    steps.push(step);

    const branchOutcome = (handle, label) => {
      const targetId = successorId(nodeId, handle, edges);
      if (!targetId) throw new Error(`Connect both outputs of this ${label} before saving.`);
      return { goto: reach(targetId, null) }; // routes to the target chain (its own steps); diverging allowed
    };

    if (node.type === 'logic-goal') {
      if (!node.cfg.event) throw new Error('Pick a goal event for this Goal Check.');
      step.goal_check = { event: node.cfg.event };
      step.next = reach(successorId(nodeId, 'out', edges), null);
    } else if (node.type === 'logic-condition') {
      if (!CONDITION_TYPES.includes(node.cfg.condType)) throw new Error(`"${node.cfg.condType}" isn't evaluated by the journey engine yet — pick a supported condition or remove this Condition Split.`);
      const ct = node.cfg.condType;
      if (ct === 'if_delivered' || ct === 'if_failed') {
        if (!prevActionStepId) throw new Error('"Previous step delivered" / "Previous step failed" must directly follow an action step (Push, SMS, Email, WhatsApp, or RCS).');
        step.ref_step_id = prevActionStepId;
      } else if (ct === 'event_performed' || ct === 'event_not_performed') {
        if (!node.cfg.event) throw new Error('Pick an event for this Condition Split (Event performed / not performed).');
        step.check = {
          event: node.cfg.event,
          ...(node.cfg.windowValue ? { window: { value: Number(node.cfg.windowValue), unit: node.cfg.windowUnit || 'days' } } : {}),
          properties: propFiltersToProperties(node.cfg.propFilters),
        };
      } else if (ct === 'user_property') {
        if (!node.cfg.attrField) throw new Error('Pick a user attribute for this Condition Split.');
        step.check = { field: node.cfg.attrField, operator: node.cfg.attrOperator || 'equals', value: node.cfg.attrValue };
      }
      step.condition = ct;
      step.branches = { true: branchOutcome('yes', 'Condition Split'), false: branchOutcome('no', 'Condition Split') };
    } else if (node.type === 'logic-ab') {
      step.ab_split = { split_a: Number(node.cfg.splitA) || 50 };
      step.branches = { a: branchOutcome('a', 'A/B Split'), b: branchOutcome('b', 'A/B Split') };
    } else if (ACTION_NODE_TO_CHANNEL[node.type]) {
      step.action = 'send_message';
      step.channel = ACTION_NODE_TO_CHANNEL[node.type];
      step.template_id = node.id;
      step.template = buildTemplate(node.type, node.cfg);
      if (node.cfg.attributionLinkId) step.attribution_link_id = node.cfg.attributionLinkId;
      if (node.cfg.repeatEnabled) step.repeat = buildRepeat(node.cfg);
      if (node.cfg.sendWindowEnabled) step.send_window = buildSendWindow(node.cfg);
      step.next = reach(successorId(nodeId, 'out', edges), node.id); // this action becomes "previous action" downstream
    } else {
      skipped.push(node.type);
      step.next = reach(successorId(nodeId, 'out', edges), prevActionStepId);
    }
    compiling.delete(nodeId);
    return num;
  }

  // Compile from the entry node's output — the first real node becomes step 1
  // (enrollment lands there by default).
  reach(successorId(entryNode.id, 'out', edges), null);

  const hasAction = steps.some((s) => s.action === 'send_message' || (s.branches && Object.values(s.branches).some((b) => b && b.action)));
  if (!hasAction) {
    throw new Error('Add at least one action step (Push, SMS, Email, WhatsApp, or RCS).');
  }

  const orphans = nodes.filter((n) => !reached.has(n.id));
  if (orphans.length) {
    skipped.push(...orphans.map((n) => n.type));
  }

  const ui = buildUiSnapshot(nodes, edges, viewport);
  ui.node_steps = nodeSteps;       // nodeId -> step number, for Live View metric overlay
  ui.entry_node_id = entryNode.id; // so the report can attribute admissions to the entry node
  const globalExit = buildGlobalExit(entryNode.cfg || {});
  const sendingPolicy = buildSendingPolicy(entryNode.cfg || {});
  return {
    config: {
      trigger, steps, ui,
      ...(globalExit ? { global_exit: globalExit } : {}),
      ...(sendingPolicy ? { sending_policy: sendingPolicy } : {}),
    },
    skipped,
  };
}

// Reverse-maps a stored trigger back into the unified Entry node (best-effort,
// only used when a journey has no saved config.ui snapshot — otherwise the exact
// builder graph is restored verbatim). Old entry-event/schedule/segment configs
// collapse into the one Entry node with the matching mode.
function entryNodeFromTrigger(trigger, nextId) {
  const type = trigger?.type || 'segment';
  const base = defaultCfg('entry');
  const common = { reEntry: trigger?.re_entry || 'once_ever', context: trigger?.context || '' };
  const genId = nextId || (() => 'p' + Math.random().toString(36).slice(2, 9));
  if (type === 'event') {
    return {
      type: 'entry',
      cfg: {
        ...base, ...common, mode: 'event',
        event: trigger.event_name || '',
        lookbackValue: String(trigger.lookback_window?.value || 7),
        lookbackUnit: trigger.lookback_window?.unit || 'days',
        propFilters: propertiesToPropFilters(trigger.properties, genId),
      },
    };
  }
  if (type === 'schedule') {
    return { type: 'entry', cfg: { ...base, ...common, mode: 'schedule', cron: trigger.cron_expression || '', tz: trigger.timezone || 'IST +5:30', segmentId: trigger.segment_id || '' } };
  }
  return { type: 'entry', cfg: { ...base, ...common, mode: 'segment', segmentId: trigger?.segment_id || '', membershipMode: trigger?.membership_mode || 'current_and_future' } };
}

function waitUntilToCfg(waitUntil) {
  return {
    rule: waitUntil.rule,
    tz: waitUntil.tz || 'IST +5:30',
    time: waitUntil.time || '09:00',
    days: String(waitUntil.days ?? '1'),
    weekday: String(waitUntil.weekday ?? '1'),
    dayOfMonth: String(waitUntil.day_of_month ?? '1'),
    daysBeforeEnd: String(waitUntil.days_before_end ?? '0'),
    customDate: waitUntil.custom?.date || '',
    customTime: waitUntil.custom?.time || '',
  };
}

// Best-effort reverse mapping for loading an existing journey back into the
// canvas — reconstructs the entry node (matching the trigger type), one node
// per step (plus a preceding wait-relative/wait-absolute node, if the step
// had a wait_after/wait_until), a branch node with its two action nodes (or
// a direct pass-through edge, for a branch with no action) for any
// condition/ab_split step, and the edges connecting all of them in order.
export function journeyConfigToNodes(config) {
  let nodeCounter = 1;
  let edgeCounter = 1;
  const nextId = () => 'n' + nodeCounter++;
  const nextEdgeId = () => 'e' + edgeCounter++;
  const nodes = [];
  const edges = [];
  let col = 0;
  let row = 0;
  const place = () => {
    const pos = { x: 60 + col * 240, y: 60 + row * 160 };
    col = (col + 1) % 3;
    if (col === 0) row++;
    return pos;
  };
  const link = (fromId, toId, fromHandle) => {
    edges.push({ id: nextEdgeId(), from: fromId, to: toId, fromHandle: fromHandle || 'out' });
  };
  // Returns the id+handle that should feed into whatever comes next: a new
  // action node fed from (branchId, handle) if this branch has one, or
  // (branchId, handle) itself again when there's no action — the branch
  // node's own handle becomes a direct pass-through edge to the next node.
  const resolveBranchEnd = (action, branchId, handle) => {
    if (!action) return { from: branchId, handle };
    const id = nextId();
    nodes.push({ id, type: CHANNEL_TO_ACTION_NODE[action.channel] || 'action-push', ...place(), cfg: templateToCfg(action.channel, action.template, action.attribution_link_id) });
    link(branchId, id, handle);
    return { from: id, handle: 'out' };
  };

  const entryId = nextId();
  const entry = entryNodeFromTrigger(config?.trigger, nextId);
  nodes.push({ id: entryId, type: entry.type, ...place(), cfg: entry.cfg });
  let prevId = entryId;
  let prevHandle = 'out';
  let pendingBranchEnds = null; // [{from, handle}, ...]

  const linkPending = (toId) => {
    if (!pendingBranchEnds) return false;
    pendingBranchEnds.forEach(({ from, handle }) => link(from, toId, handle));
    pendingBranchEnds = null;
    return true;
  };

  for (const step of config?.steps || []) {
    let waitFromId = prevId;
    let waitFromHandle = prevHandle;

    if (step.wait_after) {
      const waitId = nextId();
      nodes.push({ id: waitId, type: 'wait-relative', ...place(), cfg: { amount: String(step.wait_after.value), unit: step.wait_after.unit, waitType: 'Fixed duration' } });
      if (!linkPending(waitId)) link(waitFromId, waitId, waitFromHandle);
      waitFromId = waitId; waitFromHandle = 'out';
    }
    if (step.wait_until) {
      const waitId = nextId();
      nodes.push({ id: waitId, type: 'wait-absolute', ...place(), cfg: waitUntilToCfg(step.wait_until) });
      if (!linkPending(waitId)) link(waitFromId, waitId, waitFromHandle);
      waitFromId = waitId; waitFromHandle = 'out';
    }
    prevId = waitFromId;
    prevHandle = waitFromHandle;

    if (step.goal_check) {
      const goalId = nextId();
      nodes.push({ id: goalId, type: 'logic-goal', ...place(), cfg: { event: step.goal_check.event } });
      if (!linkPending(goalId)) link(prevId, goalId, prevHandle);
      prevId = goalId; prevHandle = 'out';
      continue;
    }

    if (step.condition || step.ab_split) {
      const isAb = !!step.ab_split;
      const branchId = nextId();
      nodes.push({
        id: branchId,
        type: isAb ? 'logic-ab' : 'logic-condition',
        ...place(),
        cfg: isAb
          ? { splitA: String(step.ab_split.split_a ?? 50), splitB: String(100 - (step.ab_split.split_a ?? 50)) }
          : { condType: step.condition, event: '', timeWindow: 'ever' },
      });
      if (!linkPending(branchId)) link(prevId, branchId, prevHandle);

      const [handleA, handleB] = isAb ? ['a', 'b'] : ['yes', 'no'];
      const [actionA, actionB] = isAb ? [step.branches?.a, step.branches?.b] : [step.branches?.true, step.branches?.false];

      // The next step's node becomes both branches' target — stash both
      // "from" ends and connect them once that node exists.
      pendingBranchEnds = [
        resolveBranchEnd(actionA, branchId, handleA),
        resolveBranchEnd(actionB, branchId, handleB),
      ];
      prevId = null;
      prevHandle = null;
      continue;
    }

    const actionType = CHANNEL_TO_ACTION_NODE[step.channel] || 'action-push';
    const stepId = nextId();
    nodes.push({ id: stepId, type: actionType, ...place(), cfg: templateToCfg(step.channel, step.template, step.attribution_link_id) });
    if (!linkPending(stepId)) link(prevId, stepId, prevHandle);
    prevId = stepId;
    prevHandle = 'out';
  }

  return { nodes, edges };
}
